"use client";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";

import usePosition from "@/hooks/usePosition";
import {
  embeddedSearchQuerySubmittedEvent,
  headerSearchBarSubmittedEvent,
} from "@/lib/segment";
import defaultSuggestions from "@/mocks/defaultSuggestions.json";

import ProductSearchSuggestions from "./ProductSearchSuggestions";
import { ProductSearchContextProvider } from "./ProductSearchContextProvider";
import SearchBox from "./SearchBox";
import { Suggestions } from "./types";
import {
  EventCacheType,
  getCourseSubject,
  getProgramSubjectForTagular,
  resetEventCache,
  getWebContext,
} from "@/utils/tagular/helpers";
import { ProcessedCourse } from "@/common/course/types";
import { Program } from "@/common/program/types";
import { PacingType } from "@/common/program/types";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { useInView } from "react-intersection-observer";
import useFormEvents from "@/hooks/eventing/useFormtEvents";
import { FieldInputted } from "@/hooks/eventing/types/redventures.usertracking.v3.FieldInputted";
import { getCorrelationID } from "@/utils/tagular/helpers";
import { getPageType } from "@/utils/tagular/helpers";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import { createSearchQueryId } from "@/utils/getSearchQueryId";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import useProductSearch from "@/monarch/rules/productSearch/useProductSearch";
import useIsMobile, { DEFAULT_BREAKPOINT_WIDTH } from "@/hooks/useIsMobile";
import useScrollLock from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import ProductSuggestionsV2 from "./components/v2/ProductSuggestions";
import {
  DROPDOWN_MAX_WIDTH_PX,
  DROPDOWN_MIN_WIDTH_PX,
  DROPDOWN_VIEWPORT_MARGIN_PX,
  MOBILE_NAV_HEIGHT_PX,
  PRODUCT_SEARCH_REVAMP_PLACEHOLDER,
} from "./components/v2/constants";
import useTranslations from "@/i18n/useTranslations";

export interface ProductSearchProps {
  placeholder: string;
  button?: string;
  suggestions?: Suggestions;
  variant?: string;
  embedded?: boolean;
  showButtons?: boolean;
  isRoundedButton?: boolean;
  isSearchBoxColumn?: boolean;
  course?: ProcessedCourse;
  program?: Program;
  conversionType?: string;
  location?: string;
  autoFocus?: boolean;
  searchButtonColor?: string;
  searchButtonLabelColor?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Mobile nav: full-page layout while search bar is expanded (search icon toggled). */
  isMobileSearchExpanded?: boolean;
}

export interface sendAnalyticsProps {
  fromViewAllButton?: boolean;
  input?: string | undefined;
}

const ProductSearch = ({
  searchButtonColor,
  searchButtonLabelColor,
  placeholder,
  button,
  suggestions = defaultSuggestions,
  variant = "",
  embedded = true,
  showButtons = true,
  isRoundedButton = true,
  isSearchBoxColumn = true,
  course,
  program,
  conversionType,
  location = "embedded-search",
  autoFocus = false,
  onFocus,
  onBlur,
  isMobileSearchExpanded = false,
}: ProductSearchProps) => {
  const tSearch = useTranslations("Search");
  const buttonLabel = button ?? tSearch("searchButton");
  const params = useSearchParams();
  const [showHits, setShowHits] = useState(false);
  const [dropdownMaxWidth, setDropdownMaxWidth] = useState<string | undefined>(
    undefined,
  );
  // Track if this is the first focus event
  const isFirstFocus = useRef(true);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const positionedDivRef = useRef<HTMLDivElement | null>(null);
  const position = usePosition(searchBoxRef, positionedDivRef);
  const hasViewedRef = useRef(false);

  useLayoutEffect(() => {
    if (!showHits || !searchBoxRef.current) return;

    const el = searchBoxRef.current;
    let rafId: number | null = null;

    const updateDropdownWidth = () => {
      if (window.innerWidth < DEFAULT_BREAKPOINT_WIDTH) {
        setDropdownMaxWidth(undefined);
        return;
      }

      const rect = el.getBoundingClientRect();
      const available =
        window.innerWidth - rect.left - DROPDOWN_VIEWPORT_MARGIN_PX;
      const clamped = Math.max(
        DROPDOWN_MIN_WIDTH_PX,
        Math.min(DROPDOWN_MAX_WIDTH_PX, available),
      );
      setDropdownMaxWidth(`${clamped}px`);
    };

    const scheduleUpdate = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateDropdownWidth();
      });
    };

    updateDropdownWidth();

    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(el);
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.visualViewport?.addEventListener("resize", scheduleUpdate);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
    };
  }, [showHits]);

  // get referrer URL
  let referrerUrl = "";
  if (typeof document !== "undefined" && document.referrer) {
    referrerUrl = document.referrer;
  }

  const { flagValue, isLoading: isFlagLoading } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_SEARCH_BAR_REVAMP_TEST",
    "legacy",
  );

  const isTestFlag = flagValue === "test";

  const {
    isLoading: isMonarchLoading,
    variant: monarchVariant,
    data: monarchData,
  } = useProductSearch();

  const analyticsReady = !isFlagLoading && (!isTestFlag || !isMonarchLoading);

  const isRevampSearchUi =
    analyticsReady && isTestFlag && monarchVariant === "test";

  const isMobileNavExpandedRevamp = isRevampSearchUi && isMobileSearchExpanded;

  const isMobile = useIsMobile();
  const isMobileFullPageLayout =
    isMobile && isRevampSearchUi && (isMobileNavExpandedRevamp || showHits);
  useScrollLock(isMobileFullPageLayout);

  useEffect(() => {
    if (isMobileNavExpandedRevamp) {
      setShowHits(true);
    }
  }, [isMobileNavExpandedRevamp]);

  const magnifierWebElement: ElementClicked["webElement"] = useMemo(() => {
    if (isRevampSearchUi) {
      return {
        elementType: ElementType.Button,
        htmlId: "search-button",
        location,
        text: PRODUCT_SEARCH_REVAMP_PLACEHOLDER,
        name: "nav-search-button",
      };
    }

    return {
      elementType: ElementType.Entry,
      htmlId: "search-button",
      location,
      text: "search",
      name: isTestFlag
        ? `${location}-button`
        : (conversionType ?? "hero-search"),
    };
  }, [conversionType, isRevampSearchUi, isTestFlag, location]);

  const { elementClicked, elementViewed } = useElementEvents({
    webElement: magnifierWebElement,
  });
  const { fieldInputted } = useFormEvents({});
  const handleFocus = () => {
    // Skip first focus if triggered via autoFocus
    if (isFirstFocus.current && autoFocus) {
      isFirstFocus.current = false;
      return;
    }

    resetEventCache(EventCacheType.ProductSearch);
    setShowHits(true);
    isFirstFocus.current = false;

    // Call external onFocus callback if provided
    onFocus?.();
  };

  const { ref: inViewEventRef, inView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );

  useEffect(() => {
    if (!analyticsReady) {
      hasViewedRef.current = false;
      return;
    }
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [analyticsReady, elementViewed, inView]);

  const handleBlur = () => {
    if (!isMobileNavExpandedRevamp) {
      setShowHits(false);
    }

    // Call external onBlur callback if provided
    onBlur?.();
  };

  const sendAnalytics = (data?: sendAnalyticsProps) => {
    const searchQueryId = createSearchQueryId();

    const customDimensions = createCustomDimensions([
      ["search_query_id", searchQueryId],
      ["search_query", data?.input || ""],
    ]);

    const displayMagnifierText =
      data?.input?.trim() ||
      (isRevampSearchUi ? PRODUCT_SEARCH_REVAMP_PLACEHOLDER : placeholder);

    elementClicked({
      customDimensions,
      webElement: {
        ...magnifierWebElement,
        text: displayMagnifierText,
      },
    });

    const handler = embedded
      ? embeddedSearchQuerySubmittedEvent
      : headerSearchBarSubmittedEvent;

    if (typeof window !== "undefined" && data?.input) {
      const webContextData = getWebContext();
      const fieldInputtedEvent: FieldInputted = {
        correlationId: getCorrelationID(),
        userInputField: {
          fieldName: location,
          fieldLabel: isRevampSearchUi
            ? PRODUCT_SEARCH_REVAMP_PLACEHOLDER
            : placeholder,
          fieldType: "search-bar",
          fieldValue: data?.input,
        },
        webContext: {
          ...webContextData,
          userAgent: webContextData?.userAgent ?? undefined,
          page: {
            referrer: referrerUrl,
            title: document.title,
            url: window.location.href,
            pageType: getPageType(),
          },
        },
      };
      fieldInputted(fieldInputtedEvent);
    }

    handler({
      label: searchBoxRef.current?.querySelector("input")?.value,
      fromViewAllButton: data?.fromViewAllButton,
      courseUuid: course?.productUuid,
      activeCourseRunKey: course?.activeCourseRun?.key,
      ...(course && {
        isSelfPaced:
          course.activeCourseRun?.pacingType === PacingType.SELFPACED,
      }),
      subject: program
        ? getProgramSubjectForTagular(program)
        : getCourseSubject(course),
      ...(program && {
        programUuid: program.externalId,
        path: program.marketingUrl,
      }),
    });
  };

  const LegacySearchBox = (
    <div>
      <div ref={searchBoxRef}>
        <SearchBox
          button={buttonLabel}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          variant={variant}
          onSubmit={sendAnalytics}
          showButtons={showButtons}
          isRoundedButton={isRoundedButton}
          isSearchBoxColumn={isSearchBoxColumn}
          autoFocusOnMount={autoFocus}
          searchButtonColor={searchButtonColor}
          searchButtonLabelColor={searchButtonLabelColor}
          location={location}
        />
      </div>
      <div
        className={`${
          showHits ? "my-1" : "hidden"
        } px-3 md:px-0 md:w-[504px] absolute z-50 ${
          position === "above" ? "bottom-full" : ""
        }`}
        ref={positionedDivRef}
        onMouseDown={(e) => e.preventDefault()}
      >
        <ProductSearchSuggestions
          suggestions={suggestions}
          sendAnalytics={sendAnalytics}
          location={location}
        />
      </div>
    </div>
  );

  // Revamp search box - test variant
  const RevampSearchBox = (
    <div
      ref={searchBoxRef}
      className={cn(
        "relative z-40 max-w-[633px]",
        isMobileFullPageLayout &&
          "max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:top-[var(--mobile-search-top)] max-md:z-50 max-md:flex max-md:max-w-none max-md:flex-col max-md:bg-white",
      )}
      style={
        isMobileFullPageLayout
          ? ({
              "--mobile-search-top": `${MOBILE_NAV_HEIGHT_PX}px`,
            } as React.CSSProperties)
          : undefined
      }
    >
      <div
        className={cn(
          "shrink-0 overflow-hidden border border-putty-500 bg-white px-2 md:px-0",
          isMobileFullPageLayout
            ? "rounded-t-[6px] rounded-b-none max-md:rounded-none max-md:border-x-0 max-md:border-t-0"
            : showHits
              ? "rounded-t-[6px] rounded-b-none"
              : "rounded-[6px]",
        )}
      >
        <SearchBox
          button={buttonLabel}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={tSearch("revampPlaceholder")}
          variant="revamp"
          onSubmit={sendAnalytics}
          showButtons={true}
          isRoundedButton={isRoundedButton}
          isSearchBoxColumn={isSearchBoxColumn}
          autoFocusOnMount={autoFocus}
          searchButtonColor={searchButtonColor}
          searchButtonLabelColor={searchButtonLabelColor}
          suppressInternalSearchMagnifierAnalytics={isRevampSearchUi}
          location={location}
        />
      </div>
      <div
        className={cn(
          showHits ? "block" : "hidden",
          "min-w-0 max-md:w-full",
          showHits &&
            "max-md:flex max-md:min-h-0 max-md:flex-1 max-md:flex-col",
          "md:absolute md:left-0 md:z-50",
          position === "above"
            ? "md:bottom-full md:mb-3"
            : "md:top-full md:mt-3",
        )}
        style={dropdownMaxWidth ? { width: dropdownMaxWidth } : undefined}
        ref={positionedDivRef}
        onMouseDown={(e) => e.preventDefault()}
      >
        <ProductSuggestionsV2
          featuredPrograms={monarchData?.featuredPrograms}
          featuredCategories={monarchData?.featuredCategories}
          trendingSectionHeader={monarchData?.trendingSectionHeader}
          mobileCategoryLimit={monarchData?.mobileCategoryLimit}
          learnerFavoritesSectionHeader={
            monarchData?.learnerFavoritesSectionHeader
          }
          noResultsHeader={monarchData?.noResultsHeader}
          noResultsBullets={monarchData?.noResultsBullets}
          footerText={monarchData?.footerText}
          footerUrl={monarchData?.footerUrl}
          footerLinkText={monarchData?.footerLinkText}
          typingStateStrategy={monarchData?.typingStateStrategy}
          querySuggestionsIndex={monarchData?.querySuggestionsIndex}
          location={location}
        />
      </div>
    </div>
  );

  const SearchBoxSkeleton = () => {
    return (
      <div className="md:w-72">
        <div className="w-full h-12 bg-putty-100 rounded-default-md animate-pulse" />
      </div>
    );
  };

  const renderContent = () => {
    // Both flags are loading - show skeleton
    if (isFlagLoading || (isTestFlag && isMonarchLoading)) {
      return <SearchBoxSkeleton />;
    }

    // Feature flag resolved to "legacy" - show legacy immediately
    if (!isTestFlag) {
      return LegacySearchBox;
    }

    // Feature flag is "test" and Monarch resolved to "test" - show revamp
    if (monarchVariant === "test") {
      return RevampSearchBox;
    }

    // Feature flag is "test" but Monarch resolved to "control" - show legacy
    return LegacySearchBox;
  };

  return (
    <ProductSearchContextProvider query={params.get("q") || ""}>
      <div ref={inViewEventRef} className="relative min-h-12">
        {renderContent()}
      </div>
    </ProductSearchContextProvider>
  );
};

export default ProductSearch;
